/**
 * OBS Browser Source 진입점 — Plan 05-05 Task 2.
 *
 * 역할:
 *   1) URL query 에서 streamerId/token 추출 (bootstrap.ts)
 *   2) theme 로드 (GET /api/v1/widgets/:streamerId/theme · fallback defaultTheme)
 *   3) prefers-reduced-motion 구독 → StateOrchestrator resolveMotion 으로 snap 강등
 *   4) Socket.IO `/widget` namespace 연결 (JWT auth) + createHandlers 주입
 *   5) 초기 connecting 상태 전이
 *
 * 기존 raw WebSocket 경로 제거 — socket/client.ts 로 완전 대체.
 */
import type { Theme } from '@loop/shared/theme';
import { subscribeReducedMotion, prefersReducedMotion } from './accessibility/reduced-motion.js';
import { detectViewerMode, type ViewerMode } from './viewer-mode.js';
import { installWidgetCss } from './states/render.js';
import { loadTheme } from './theme/loader.js';
import { connectWidget, type WidgetSocketHandle } from './socket/client.js';
import { TtsPlayer } from './audio/player.js';
import { StateOrchestrator } from './state/orchestrator.js';
import { createHandlers } from './events/handlers.js';
import { createGameEngine, wireDonationHandler, type GameEngine } from './games/index.js';
import { resolveGame } from './games/registry.js';
import type { GameKind } from './games/game-interface.js';

export interface BootArgs {
  readonly root: HTMLElement;
  readonly streamerId: string;
  readonly token: string;
  readonly apiBase: string;
  readonly wsBase: string;
}

export interface BootResult {
  disconnect(): void;
  theme: Theme;
  mode: ViewerMode;
}

/**
 * 위젯 부팅.
 *
 * 에러 내성:
 *   - theme 실패 → defaultTheme (loadTheme 이 폴백)
 *   - WS 연결 실패 → connectWidget 이 disconnected-fatal 상태 발행
 *   - reducedMotion · matchMedia 미지원 → subscribeReducedMotion 이 no-op 반환
 */
export async function boot(args: BootArgs): Promise<BootResult> {
  installWidgetCss();

  const mode = detectViewerMode();
  let reducedMotion = prefersReducedMotion();

  // theme 먼저 — 이후 모든 render 가 이 theme 기반
  const theme = await loadTheme(args.streamerId, args.apiBase);

  // reducedMotion 구독 (runtime 토글 반영 — DESIGN §10.4)
  subscribeReducedMotion((rm) => {
    reducedMotion = rm;
  });

  const orchestrator = new StateOrchestrator({
    host: args.root,
    theme,
    mode,
    getReducedMotion: (): boolean => reducedMotion,
  });

  // 초기 상태 = connecting (DESIGN §3.1 #2). 실제로 public 모드에선 투명.
  await orchestrator.transitionTo({ kind: 'connecting' });

  const tts = new TtsPlayer();
  const eventHandlers = createHandlers({
    state: orchestrator,
    tts,
    theme,
    apiBase: args.apiBase,
  });

  // WS state 와 domain event 는 동일한 handlers 객체로 전달. onState 는
  // connectWidget 이 직접 발행 (connecting → idle / reconnecting / disconnected-fatal).
  // 따라서 onState 를 orchestrator.transitionTo 로 덮어씀.
  const handle: WidgetSocketHandle = connectWidget({
    streamerId: args.streamerId,
    token: args.token,
    wsBase: args.wsBase,
    handlers: {
      ...eventHandlers,
      onState: (next) => void orchestrator.transitionTo(next),
    },
  });

  // ── Plan 08-02 / GAME-01: Optional PixiJS game engine bootstrap ────────
  //
  // 게임 엔진은 조건부 활성화: (1) DOM 에 #game-canvas 요소가 있고
  // (2) streamerConfig.gamesEnabled=true 인 경우만 부팅.
  //
  // Wave 0 위젯 동작은 완전한 BC — canvas 요소가 없으면 이 블록은 no-op.
  // 08-04 UI 가 canvas element 를 page 에 추가하고 streamerConfig 를 내려줌.
  let gameEngine: GameEngine | null = null;
  let unwireGame: (() => void) | null = null;
  try {
    const gameCanvas = document.getElementById('game-canvas');
    const gamesEnabled = Boolean((globalThis as { __LOOP_GAMES_ENABLED__?: boolean }).__LOOP_GAMES_ENABLED__);
    if (gameCanvas instanceof HTMLCanvasElement && gamesEnabled) {
      gameEngine = await createGameEngine(gameCanvas, {
        reducedMotion,
      });
      unwireGame = wireDonationHandler(handle.socket, gameEngine);

      // ── Plan 08-06 Task 1 (GAME-05 integration): Active game 등록 ─────────
      //
      // BFF `/api/widget/games` 에서 3 kind 의 config 를 받아, first-enabled
      // kind 를 resolveGame 으로 인스턴스화 후 engine.registerGame 한다.
      // Wave 1 단순화 — 여러 kind 가 enabled 여도 first (roulette → slot → race
      // 순서의 BFF dict 순회) 만 활성. Wave 2 다중 game 오케스트레이션 후보.
      //
      // fetch 실패 / 게임 미활성 / resolve 실패 시 engine 만 살아있고 game 미등록
      // 상태로 유지 (dispatch 시 warn drop, BC 완전 보존).
      try {
        // HI-01: OBS Browser Source 는 cross-origin + no-cookie 환경 → widget JWT
        // 토큰 기반 public endpoint 사용. `/api/widget/games` (세션 기반) 는 OBS
        // 에서 쿠키 미전송으로 401 silent-fail.
        const resp = await fetch(
          `${args.apiBase}/api/v1/widget/games/public/${encodeURIComponent(args.streamerId)}`,
          {
            headers: {
              accept: 'application/json',
              authorization: `Bearer ${args.token}`,
            },
          },
        );
        if (resp.ok) {
          const body = (await resp.json()) as {
            games?: Record<
              GameKind,
              { enabled: boolean; configJson: unknown; requiresAdultVerification?: boolean }
            >;
          };
          const games = body.games ?? null;
          if (games) {
            const kinds: readonly GameKind[] = ['roulette', 'slot', 'race'];
            const activeKind = kinds.find((k) => games[k]?.enabled === true) ?? null;
            if (activeKind && gameEngine) {
              const cfg = games[activeKind].configJson;
              const game = resolveGame(activeKind, cfg);
              await gameEngine.registerGame(game);
            }
          }
        }
      } catch (gameErr) {
        console.warn('[widget] active game register skipped', gameErr);
      }
    }
  } catch (err) {
    console.warn('[widget] game engine bootstrap failed — 위젯 기본 동작은 유지', err);
  }

  return {
    disconnect: (): void => {
      if (unwireGame) {
        unwireGame();
        unwireGame = null;
      }
      if (gameEngine) {
        gameEngine.destroy();
        gameEngine = null;
      }
      handle.disconnect();
      tts.stop();
    },
    theme,
    mode,
  };
}
